import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { TripInfoComponent, type TripPackageDetails } from '../types';
import TripSegment from '../TripSegment';
import TripPackage from '../TripPackage';
import TrainPriceType from './TrainPriceType';
import TrainPriceClass from './TrainPriceClass';
import Train from './Train';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import { type CardLabel, type PassengerCount } from '@itinerary/types';
import logos from '@static/availableTrainLogos.json';
import { ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';
import { PaxType } from '@utils/types';
import { type SellType, SellTypeCode, trainSellTypes } from '@utils/train';
import { Gender } from '@store/form';
import { type AvailableTrain } from '@clients/TrainApi';

export default class TrainPackage extends TripPackage<Train, TrainPriceClass> {
    override reserveType: ReserveType = ReserveType.Train;

    override get tripPackageDetails(): TripPackageDetails[] {
        return [
            ...super.tripPackageDetails,
            {
                title: 'ایستگاه‌های بین‌راهی',
                component: TripInfoComponent.TrainMidStations,
            },
        ];
    }

    tripType: TripType = TripType.OneWay;

    isForRoundTrip = false;

    override needRefreshInvoiceOnChangeNationality = true;

    /**
     * Points of the trip packages, based on passengers' opinions
     */
    override points: number | null = null;

    override prices: TrainPriceType[] = [];

    override totalCount = 0;

    override segments: TripSegment<Train>[] = [];

    override international = false;

    defaultSellType: SellType | null = null;

    exclusive = false;

    override get gateWayMessage(): string {
        return 'با توجه به حذف توقف در برخی ایستگاه‌های بین‌راهی، پیاده و سوار شدن صرفا در ایستگاه‌هایی که بر روی بلیط ثبت شده امکان‌پذیر است.';
    }

    override get cancellationMessage() {
        return 'طبق دستورالعمل راه‌آهن، وجه بلیط استردادشده اینترنتی تنها به شماره کارتی واریز می‌شود که نام دارنده آن با نام یکی از مسافران مندرج در بلیط مطابقت داشته باشد. در غیر این صورت، امکان کنسلی آنلاین وجود نداشته و مسافر باید برای انجام کنسلی به مراکز حضوری مراجعه کند. این محدودیت از سوی راه‌آهن تعیین شده و خارج از اختیار مستربلیط است.';
    }

    override needsLoginForDiscount = false;

    override key = '';

    override passengerCountEditTypes = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
        },
        {
            code: PaxType.Child,
            title: 'کودک',
        },
    ];

    override callToReserve = false;

    get totalReserveCount() {
        if (!this.exclusive || !this.defaultPrice?.isCompartment) {
            return this.totalCount;
        }
        return this.defaultPrice.getExclusiveNeedCapacity(this.totalCount);
    }

    constructor(
        train?: AvailableTrain,
        totalCount = 0,
        sellTypeCode = 0,
        exclusive = false,
        defaultPriceIndex = 0,
        minPersons = 1,
        priceId = 0,
        tripType: TripType = TripType.OneWay,
        score?: number
    ) {
        super();
        if (!train) {
            return;
        }
        this.concreteType = ReserveType.Train;
        this.key = `${train.id}:${priceId}`;
        this.uniqueId = `${train.id}:${priceId}`;

        this.tripType = tripType;
        this.totalCount = totalCount;
        this.minPersons = minPersons;
        this.prices =
            train.prices?.map(p => {
                const sellType = trainSellTypes.find(
                    t => t.code === p.sellType
                );
                if (!sellType) {
                    throw 0;
                }
                return new TrainPriceType(
                    sellType,
                    p.classes?.map(
                        c =>
                            new TrainPriceClass(
                                c,
                                train.isForeign,
                                sellType ?? [],
                                exclusive,
                                totalCount
                            )
                    ) ?? []
                );
            }) ?? [];

        const currentClass = this.prices.find(
            p => p.sellType.code === sellTypeCode
        );
        if (!currentClass) {
            throw 0;
        }
        this.defaultSellType = currentClass.sellType;

        this.defaultPrice = this.selectedPrice =
            currentClass.classes[defaultPriceIndex];
        this.cancellationTerms =
            this.defaultPrice?.price?.cancellationTermEntries?.map(entry => ({
                title: entry.title ?? '',
                amount: ![null, undefined, -1].includes(entry.percentAmount)
                    ? `٪${entry.percentAmount}`
                    : formatMoney(entry.amount),
                isFull: entry.percentAmount === 100,
                isZero: entry.percentAmount === 0 || entry.amount === 0,
            })) || [];
        this.isForRoundTrip = this.defaultPrice.isForRoundTrip;
        this.international = !!train.isForeign;
        this.exclusive = exclusive;
        this.points = score ?? train.score ?? 0;
        this.facilities = {
            title: 'کوپه',
            list: this.defaultPrice.wagonService.map(
                ({ title, hasService }) => ({
                    title: `${title} ${hasService ? 'دارد' : 'ندارد'}`,
                    check: !!hasService,
                })
            ),
        };
        const trips = [new Train(train, this.defaultPrice, logos)];
        this.segments = [
            new TripSegment(
                trips,
                [...(this.pointOption ? [this.pointOption] : [])],
                trips.length === 1
                    ? 'بدون توقف'
                    : `${
                          trips.length +
                          trips.reduce((acc, t) => acc + t.stops, 0) -
                          1
                      } توقف در ${`${trips
                          .slice(1)
                          .map(t => t.originCityName)
                          .join(' ' + 'و')} `}`
            ),
        ];
    }

    override get labels(): CardLabel[] {
        const labels: CardLabel[] = [];
        if (this.defaultPrice?.styledWagonName) {
            labels.push({
                title: this.defaultPrice.styledWagonName,
            });
        }
        if (this.isForRoundTrip) {
            labels.push({
                title: 'ویژه دوطرفه',
                backgroundColor: 'var(--cyan-200)',
                icon: faAngleRight,
                color: 'black',
            });
        }
        if (this.defaultPrice?.hasSpecialDiscount) {
            labels.push({
                title: 'تخفیف ویژه',
                backgroundColor: 'var(--green-400)',
                icon: faStar,
                color: 'black',
            });
        }
        return labels;
    }

    override get isFull() {
        return (
            !!this.defaultPrice && this.defaultPrice.capacity < this.totalCount
        );
    }

    override get reservable() {
        if (this.defaultPrice?.capacity === 0) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this.defaultPrice && !this.defaultPrice.available) {
            return false;
        }
        return true;
    }

    override get detailsMessage() {
        if (this.isForRoundTrip) {
            return 'ویژه دوطرفه: این بلیط مخصوص خریدهایی است که در حالت «رفت و برگشت» انجام می‌شود. با توجه به اعمال تخفیف ویژه دوطرفه، امکان استرداد بلیط به‌صورت یک‌طرفه وجود ندارد.';
        } else {
            return null;
        }
    }

    override get alertText() {
        if (
            this.reservable &&
            this.defaultPrice &&
            this.totalCount !== this.totalReserveCount &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت قطار انتخابی کمتر از کوپه دربست است.';
        }
        return super.alertText;
    }

    override get buyTooltip() {
        // if (this.defaultPrice && !this.defaultPrice.available) {
        // return `در حال حاضر به‌دلیل قطعی ارتباط با تامین‌کننده، رزرو این سرویس مقدور نمی‌باشد.`;
        // } else if (
        // this.defaultPrice &&
        // this.defaultPrice.capacity < this.totalCount
        // ) {
        // return `ظرفیت این سرویس در حال حاضر تکمیل بوده و امکان رزرو ندارد. چنانچه ظرفیتی آزاد شود به صورت آنلاین نمایش داده خواهد شد.`;
        // } else {
        // return '';
        // }
        if (
            this.reservable &&
            this.defaultPrice &&
            this.totalCount !== this.totalReserveCount &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت قطار انتخابی کمتر از کوپه دربست است.';
        }
        return '';
    }

    override get showTooltip() {
        return true;
    }

    override get buyText() {
        if (this.defaultPrice?.capacity === 0) {
            return 'تکمیل ظرفیت';
        } else if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت ناکافی';
        } else if (this.defaultPrice && !this.defaultPrice.available) {
            return 'قطعی سرور';
        } else if (this.minPersons > this.totalCount) {
            return `حداقل تعداد ${this.minPersons} نفر برای رزرو`;
        } else if (
            this.tripType !== TripType.RoundTrip &&
            this.isForRoundTrip
        ) {
            return 'محدودیت رزرو دوطرفه';
        } else if (
            this.defaultPrice &&
            this.totalReserveCount > this.defaultPrice.compartmentCapacity
        ) {
            return 'ظرفیت کوپه';
        }
        return super.buyText;
    }

    override get reserveError() {
        if (this.tripType !== TripType.RoundTrip && this.isForRoundTrip) {
            return `این بلیط تنها در حالت خرید «رفت و برگشت» قابل انتخاب است.`;
        }
        if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت موجود کمتر از تعداد مسافران شماست!';
        }
        if (
            this.defaultPrice &&
            this.totalReserveCount > this.defaultPrice.compartmentCapacity
        ) {
            return `تعداد مسافرین انتخابی از تعداد ظرفیت هر کوپه بیشتر است.لطفا با تغییر جستجو تعداد مسافرین خود را به ${
                this.defaultPrice.compartmentCapacity
            }نفر تغییر دهید.و برای ${
                this.totalReserveCount - this.defaultPrice.compartmentCapacity
            }مسافر دیگر به صورت جداگانه خرید نمایید.`;
        }
        return super.reserveError;
    }

    override get showReserveErrorInDesktop() {
        return this.tripType !== TripType.RoundTrip && this.isForRoundTrip;
    }

    override checkNeedsRaiseClass(count: PassengerCount) {
        if (this.isForRoundTrip) {
            return false;
        }
        return super.checkNeedsRaiseClass(count);
    }

    override get hasGenderLimitation() {
        const sellCode = this.defaultSellType?.code;
        switch (sellCode) {
            case SellTypeCode.Male:
                return {
                    gender: Gender.Male,
                    ignorable: true,
                    message:
                        'شما در حال خرید از سهمیه برادران هستید. در صورت عدم رعایت قوانین سهمیه، مِستربلیط مسئولیتی در قبال عواقب احتمالی آن برعهده نخواهد داشت.',
                };
            case SellTypeCode.Female:
                return {
                    gender: Gender.Female,
                    ignorable: true,
                    message:
                        'شما در حال خرید از سهمیه خواهران هستید. در صورت عدم رعایت قوانین سهمیه، مِستربلیط مسئولیتی در قبال عواقب احتمالی آن برعهده نخواهد داشت.',
                };
            default:
                return false;
        }
    }

    override get canTrackPackageCapacity(): boolean {
        if (this.defaultSellType?.code === SellTypeCode.Car) {
            return false;
        }
        return super.canTrackPackageCapacity;
    }

    override get reserveButtonEnabled() {
        if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return false;
        } else if (
            this.tripType !== TripType.RoundTrip &&
            this.isForRoundTrip
        ) {
            return false;
        } else if (this.totalReserveCount > this.capacity) {
            return false;
        } else if (this.minPersons > this.totalCount) {
            return false;
        } else if (
            this.defaultPrice &&
            this.totalReserveCount > this.defaultPrice.compartmentCapacity
        ) {
            return false;
        }
        return super.reserveButtonEnabled;
    }

    override get order() {
        return new ItineraryOrder(
            this.departureDateTime.getTime(),
            (this.defaultPrice && this.defaultPrice.singlePrice) || 0
        );
    }

    /**
     * default price id of this train package is train capacity id
     */
    get trainCapacityId() {
        return this.defaultPrice!.id;
    }

    override get raisePriceClassesTitle() {
        return 'نوع واگن';
    }

    override get raisePriceTypesTitle() {
        return 'نوع کوپه';
    }
}
